import { useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Package, Search } from 'lucide-react';
import { Kit, KitItem } from '@/types/kit';
import { Input } from '@/components/ui/input';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from '@/components/ui/command';
import { GearCategory } from '@/types/gear';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useCurrency } from '@/contexts/CurrencyContext';
import { formatCurrency } from '@/lib/utils';

interface AddFromInventoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAddItems: (items: any[]) => void;
  projectId: string;
}

export function AddFromInventoryDialog({
  open,
  onOpenChange,
  onAddItems,
  projectId,
}: AddFromInventoryDialogProps) {
  const { t } = useTranslation();
  const [selectedItems, setSelectedItems] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState<string>('items');
  const [searchQuery, setSearchQuery] = useState('');
  
  const { currency } = useCurrency();

  // Fetch inventory items
  const { data: inventoryItems, isLoading: isLoadingItems } = useQuery({
    queryKey: ['inventory-items'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('inventory_items')
        .select('*')
        .order('category')
        .order('name');
      
      if (error) {
        console.error('Error fetching inventory items:', error);
        toast.error(t('failed_load_inventory_items'));
        throw error;
      }
      
      return data || [];
    },
    enabled: open,
  });

  // Fetch kits
  const { data: kits, isLoading: isLoadingKits } = useQuery({
    queryKey: ['user-kits'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('kits')
        .select(`
          *,
          kit_items(
            *,
            inventory_items(name, category, cost_per_unit)
          )
        `);
      
      if (error) {
        console.error('Error fetching kits:', error);
        toast.error(t('failed_load_gear_kits'));
        throw error;
      }
      
      return data || [];
    },
    enabled: open,
  });

  const handleSelectItem = (item: any) => {
    // Check if item is already selected
    const existingItemIndex = selectedItems.findIndex(
      (selectedItem) => selectedItem.id === item.id
    );

    if (existingItemIndex >= 0) {
      // If item is already selected, remove it (deselect)
      const updatedItems = [...selectedItems];
      updatedItems.splice(existingItemIndex, 1);
      setSelectedItems(updatedItems);
    } else {
      // Add new item with default quantity 1
      setSelectedItems([
        ...selectedItems,
        {
          ...item,
          quantity: 1,
        },
      ]);
    }
  };

  const isItemSelected = (itemId: string) => {
    return selectedItems.some(item => item.id === itemId);
  };

  const handleSelectKit = (kit: Kit & { kit_items: KitItem[] }) => {
    // Extract all items from the kit and add them
    const kitItems = kit.kit_items.map(kitItem => {
      const inventoryItem = kitItem.inventory_items;
      if (!inventoryItem) return null;
      
      return {
        id: kitItem.inventory_item_id,
        name: inventoryItem.name,
        category: inventoryItem.category,
        cost_per_unit: inventoryItem.cost_per_unit,
        quantity: kitItem.quantity,
        notes: `From kit: ${kit.name}`,
      };
    }).filter(Boolean);
    
    // Add all kit items to the selected items
    setSelectedItems(prev => [...prev, ...kitItems]);
  };

  const handleAddToProject = () => {
    console.log('Adding items to project:', selectedItems);
    
    if (selectedItems.length === 0) {
      toast.warning(t('no_items_selected'));
      return;
    }
    
    // Prepare items for adding to project
    const itemsForProject = selectedItems.map(item => ({
      id: item.id,
      name: item.name,
      category: item.category,
      cost_per_unit: item.cost_per_unit || 0,
      quantity: item.quantity || 1,
      notes: item.notes || '',
    }));
    
    // Call the onAddItems function provided by the parent component
    onAddItems(itemsForProject);
    
    // Reset state and close dialog
    setSelectedItems([]);
    onOpenChange(false);
    
    toast.success(t('items_added_to_project', { count: itemsForProject.length }));
  };

  // Group items by category
  const groupedItems = useMemo(() => {
    if (!inventoryItems) return {};
    
    return inventoryItems.reduce((groups: Record<string, any[]>, item) => {
      const category = item.category;
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(item);
      return groups;
    }, {});
  }, [inventoryItems]);

  // Filter items based on search query
  const filteredGroups = useMemo(() => {
    if (!searchQuery.trim()) return groupedItems;
    
    const filtered: Record<string, any[]> = {};
    const lowerQuery = searchQuery.toLowerCase();
    
    Object.entries(groupedItems).forEach(([category, items]) => {
      const filteredItems = items.filter(item => 
        item.name.toLowerCase().includes(lowerQuery) || 
        item.category.toLowerCase().includes(lowerQuery)
      );
      
      if (filteredItems.length > 0) {
        filtered[category] = filteredItems;
      }
    });
    
    return filtered;
  }, [groupedItems, searchQuery]);

  // Get flattened list of all items for autocomplete
  const allItems = useMemo(() => {
    if (!inventoryItems) return [];
    return inventoryItems;
  }, [inventoryItems]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>{t('add_from_inventory')}</DialogTitle>
        </DialogHeader>

        <Tabs 
          value={activeTab} 
          onValueChange={setActiveTab}
          className="flex-1 flex flex-col overflow-hidden"
        >
          <TabsList className="w-full">
            <TabsTrigger value="items" className="flex-1">{t('inventory_items')}</TabsTrigger>
            <TabsTrigger value="kits" className="flex-1">{t('gear_kits')}</TabsTrigger>
          </TabsList>
          
          <TabsContent value="items" className="flex-1 overflow-hidden flex flex-col mt-4">
            <div className="mb-4">
              <Command className="rounded-lg border shadow-md">
                <CommandInput 
                  placeholder={t('search_items_placeholder')} 
                  className="h-10"
                  value={searchQuery}
                  onValueChange={setSearchQuery}
                />
                
                {searchQuery.trim() !== '' && (
                  <CommandList className="max-h-[200px] overflow-auto">
                    <CommandEmpty>{t('no_matching_items_found')}</CommandEmpty>
                    {allItems.length > 0 && (
                      <CommandGroup>
                        {allItems
                          .filter(item => 
                            item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                            item.category.toLowerCase().includes(searchQuery.toLowerCase())
                          )
                          .map(item => (
                            <CommandItem 
                              key={item.id}
                              onSelect={() => handleSelectItem(item)}
                              className={`flex justify-between items-center cursor-pointer ${
                                isItemSelected(item.id) 
                                  ? 'bg-primary/10 border-l-4 border-primary pl-2' 
                                  : ''
                              }`}
                            >
                              <div>
                                <div className="font-medium">{item.name}</div>
                                <div className="text-xs text-muted-foreground">{item.category}</div>
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {formatCurrency(item.cost_per_unit, currency)}/day
                              </div>
                            </CommandItem>
                          ))
                        }
                      </CommandGroup>
                    )}
                  </CommandList>
                )}
              </Command>
            </div>

            <div className="flex-1 overflow-auto border p-4 rounded-md">
              {isLoadingItems ? (
                <div className="text-center py-8">{t('loading_items')}</div>
              ) : (
                <div className="space-y-6">
                  {Object.keys(filteredGroups).length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      {searchQuery.trim() !== '' ? t('no_matching_items_found') : t('no_inventory_items_found')}
                    </div>
                  ) : (
                    Object.entries(filteredGroups).map(([category, items]) => (
                      <div key={category} className="space-y-3">
                        <h3 className="text-sm font-semibold text-white/80 flex items-center gap-2">
                          <div className="h-2 w-2 rounded-full bg-primary/80"></div>
                          {category} <span className="text-xs text-white/60">({items.length})</span>
                        </h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                          {items.map((item) => (
                            <Button
                              key={item.id}
                              variant="outline"
                              onClick={() => handleSelectItem(item)}
                              className={`justify-start h-auto py-3 px-4 transition-colors ${
                                isItemSelected(item.id) 
                                  ? 'bg-primary/20 border-l-4 border-primary' 
                                  : 'hover:bg-primary/10'
                              }`}
                            >
                              <div className="text-left w-full">
                                <div className="font-medium truncate">{item.name}</div>
                                <div className="flex justify-between items-center mt-1">
                                  <div className="text-xs text-muted-foreground">
                                    {t('qty_label', { qty: item.quantity || '1' })}
                                  </div>
                                  <div className="text-xs text-muted-foreground">
                                    {t('cost_per_day_label', { cost: item.cost_per_unit || 0 })}
                                  </div>
                                </div>
                              </div>
                            </Button>
                          ))}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </TabsContent>
          
          <TabsContent value="kits" className="flex-1 overflow-auto mt-4 border p-4 rounded-md">
            {isLoadingKits ? (
              <div className="text-center py-8">{t('loading_kits')}</div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {kits?.map((kit) => (
                  <div 
                    key={kit.id} 
                    className="border rounded-lg p-4 cursor-pointer hover:bg-accent/50 transition-colors"
                    onClick={() => handleSelectKit(kit)}
                  >
                    <div className="flex items-center gap-2">
                      <Package className="h-5 w-5" />
                      <h3 className="font-medium">{kit.name}</h3>
                    </div>
                    
                    {kit.notes && (
                      <p className="text-sm text-muted-foreground mt-1">{kit.notes}</p>
                    )}
                    
                    <div className="mt-3 space-y-1">
                      <p className="text-sm font-medium">{t('items_label')}</p>
                      <ul className="text-xs space-y-1">
                        {kit.kit_items.map(item => (
                          <li key={item.id} className="flex justify-between">
                            <span>{item.inventory_items?.name}</span>
                            <span>x{item.quantity}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ))}
                
                {kits?.length === 0 && (
                  <div className="col-span-full text-center py-8 text-muted-foreground">
                    <Package className="h-12 w-12 mx-auto mb-3 opacity-30" />
                    <p>{t('no_kits_found')}</p>
                  </div>
                )}
              </div>
            )}
          </TabsContent>
        </Tabs>

        <div className="mt-4 border-t pt-4 flex justify-between items-center">
          <div>
            <span className="text-sm font-medium">
              {t('items_selected_label', { count: selectedItems.length })}
            </span>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setSelectedItems([]);
                onOpenChange(false);
              }}
            >
              {t('cancel')}
            </Button>
            <Button
              onClick={handleAddToProject}
              disabled={selectedItems.length === 0}
            >
              {t('add_to_project')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
